import type { z } from 'zod';
import { ProblemDetailsSchema, type ProblemDetails } from '@contracts/schemas/common.schema';
import { SCREENS_HEADER, SCREENS_HEADER_VALUE } from '@/domain';
import { env } from '@/shared/config/env';
import { statusWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { getAccessToken, setAccessToken } from './tokenStore';
import { reportAnswered } from '@/application/outbox/reachability';

/**
 * The only place `fetch` appears, in `send` below. Section 3.
 *
 * It carries the access token, parses RFC 7807 errors into something the UI can
 * act on, refreshes once on a 401, and in development parses every response
 * through its schema so a mismatch with the real API surfaces as a readable
 * error naming the field rather than as `undefined is not an object` three
 * components deep. Parsing is off in production builds, section 6 item 4.
 */

/**
 * What an answer that is not a success is said as. The lab's request of
 * 9 October 2026.
 *
 * A refusal (4xx) in the server's own sentence, which is written for a person
 * and names what they may not do or what to correct; where it gave none, the
 * caller's words for what could not be done, or plain ones for the status. A
 * fault (5xx) never in the server's words, whatever they are: see
 * `statusWords`. And never the address that was asked for, which this said
 * where the server gave no sentence: "Request to /kits/K-1/start failed".
 */
function apiWords(status: number, problem: ProblemDetails | null, fallback: string | undefined): string {
  if (status >= 500) return statusWords(status);
  const detail = problem?.detail;
  if (typeof detail === 'string' && detail.trim() !== '') return detail;
  return fallback ?? statusWords(status);
}

export class ApiError extends Error {
  readonly status: number;
  readonly problem: ProblemDetails | null;
  /** Its words are for a person, so a screen may show them. See `isSaid`. */
  readonly said = true as const;

  constructor(status: number, problem: ProblemDetails | null, fallback?: string) {
    super(apiWords(status, problem, fallback));
    this.name = 'ApiError';
    this.status = status;
    this.problem = problem;
  }

  /** Field errors for a form, keyed by field name. Section 7.1. */
  get fieldErrors(): Record<string, string[]> {
    return this.problem?.errors ?? {};
  }

  /** Shown in small monospace so a photograph of a phone screen is diagnosable. */
  get traceId(): string | null {
    return this.problem?.traceId ?? null;
  }
}

/**
 * No answer at all: the request never reached the server, or its answer never
 * came back. A phone waking the installed app, a dropped connection, a server
 * restarting behind its proxy.
 *
 * `fetch` rejects for that with whatever the browser calls it: "Failed to
 * fetch", "Load failed", and on an iPhone, where a request passes through the
 * service worker, a line of the worker's own code with the address in it. That
 * text reached the sign in page of a phone at the lab on 9 October 2026. This
 * says it in one sentence instead, and keeps what the browser said as `cause`
 * for the console.
 *
 * It carries no `status`, which is how the outbox and the query client already
 * read "the network did not answer": queued and retried, exactly as before.
 */
export class NoAnswerError extends Error {
  readonly said = true as const;

  constructor(cause: unknown) {
    super(strings.errors.unreachable, { cause });
    this.name = 'NoAnswerError';
  }
}

/** How long to wait before asking again when no answer came. Long enough for a phone that has just woken. */
const ASK_AGAIN_AFTER_MS = 700;

const pause = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

/** A request that was called off on purpose is not a failure to reach the server. */
function calledOff(cause: unknown, signal: AbortSignal | null | undefined): boolean {
  return signal?.aborted === true || (cause instanceof Error && cause.name === 'AbortError');
}

/**
 * `fetch`, for `request` and for the few calls that cannot go through it: a
 * file sent as a form, a file received.
 *
 * It says "no answer" as `NoAnswerError`, and asks again `again` times first,
 * after a pause. Only where asking twice is safe: reading, signing in,
 * renewing the session. A change is not asked for twice by itself, because no
 * answer does not mean it was not made.
 */
export async function send(input: string, init: RequestInit = {}, again = 0): Promise<Response> {
  for (let asked = 0; ; asked += 1) {
    try {
      return await fetch(input, init);
    } catch (cause) {
      if (calledOff(cause, init.signal)) throw cause;
      if (asked >= again) throw new NoAnswerError(cause);
      await pause(ASK_AGAIN_AFTER_MS);
    }
  }
}

/** Thrown when the response does not match the contract. Development only. */
export class ContractViolationError extends Error {
  constructor(route: string, issues: string) {
    super(`The API response for ${route} does not match the contract.\n${issues}`);
    this.name = 'ContractViolationError';
  }
}

export interface RequestOptions<T extends z.ZodTypeAny | undefined> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** The schema to parse the response through. Omit for a 204. */
  schema?: T;
  /** Required on the work commands, section 7.4. A client generated UUID v4. */
  idempotencyKey?: string;
  /** Sent as If-Match for optimistic concurrency, section 7.9. */
  rowVersion?: string;
  signal?: AbortSignal;
  /**
   * How many more times to ask when no answer comes at all, after a short
   * pause. For a call that is safe to make twice: signing in. Left off for a
   * change, which may have been made although its answer was lost.
   */
  retryNoAnswer?: number;
  /** Internal. Prevents a refresh loop when the refresh call itself 401s. */
  skipRefresh?: boolean;
}

function url(path: string): string {
  return `${env.apiBaseUrl}${path}`;
}

async function readProblem(response: Response): Promise<ProblemDetails | null> {
  try {
    const parsed = ProblemDetailsSchema.safeParse(await response.json());
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

/**
 * A single refresh attempt shared by every caller. Ten queries failing at once
 * on a reconnect must not fire ten refreshes and invalidate each other's token.
 */
let refreshInFlight: Promise<boolean> | null = null;

async function refreshOnce(): Promise<boolean> {
  refreshInFlight ??= (async () => {
    try {
      // Asked once more when no answer comes: a phone that has just woken loses its
      // first request, and a session must not end because of it.
      const response = await send(url('/auth/refresh'), { method: 'POST', credentials: 'include' }, 1);
      if (!response.ok) return false;
      const body = (await response.json()) as { accessToken?: unknown };
      if (typeof body.accessToken !== 'string') return false;
      setAccessToken(body.accessToken);
      return true;
    } catch {
      return false;
    } finally {
      // Cleared on the next tick so concurrent callers all read this attempt.
      setTimeout(() => {
        refreshInFlight = null;
      }, 0);
    }
  })();

  return refreshInFlight;
}

export async function request<T extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<T> & { schema: T },
): Promise<z.infer<T>>;
export async function request(path: string, options?: RequestOptions<undefined>): Promise<void>;
export async function request<T extends z.ZodTypeAny>(
  path: string,
  options: RequestOptions<T | undefined> = {},
): Promise<unknown> {
  const { method = 'GET', body, schema, idempotencyKey, rowVersion, signal } = options;

  const headers = new Headers();
  if (body !== undefined) headers.set('Content-Type', 'application/json');
  // The newer screens this build can draw, so the session it is sent names no
  // screen it cannot. The lab's request of 12 October 2026: see `homeScreens`.
  headers.set(SCREENS_HEADER, SCREENS_HEADER_VALUE);

  const token = getAccessToken();
  if (token !== null) headers.set('Authorization', `Bearer ${token}`);
  if (idempotencyKey !== undefined) headers.set('Idempotency-Key', idempotencyKey);
  if (rowVersion !== undefined) headers.set('If-Match', rowVersion);

  const response = await send(
    url(path),
    {
      method,
      headers,
      // The refresh token is a cookie, so every call is credentialed.
      credentials: 'include',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      ...(signal === undefined ? {} : { signal }),
    },
    options.retryNoAnswer ?? 0,
  );
  // An answer of any kind proves the connection, whatever navigator.onLine says.
  reportAnswered();

  // 401: refresh once, then retry once. Section 7.1.
  if (response.status === 401 && options.skipRefresh !== true) {
    const refreshed = await refreshOnce();
    if (refreshed) {
      return request(path, { ...options, skipRefresh: true } as RequestOptions<T> & { schema: T });
    }
  }

  if (!response.ok) {
    throw new ApiError(response.status, await readProblem(response));
  }

  if (schema === undefined || response.status === 204) return undefined;

  const payload: unknown = await response.json();

  if (env.isProduction) return payload;

  const parsed = schema.safeParse(payload);
  if (!parsed.success) {
    const issues = parsed.error.issues
      .map((issue) => `  ${issue.path.join('.') || '(root)'}: ${issue.message}`)
      .join('\n');
    throw new ContractViolationError(`${method} ${path}`, issues);
  }

  const data: unknown = parsed.data;
  return data;
}
